import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import { Building2, Users, Briefcase, ArrowRight, Menu, X } from 'lucide-react';

const SignupSelection: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const styles = {
    nav: {
      borderBottom: '1px solid #e8eaed',
      padding: '16px 0',
      backgroundColor: '#ffffff',
    },
    navContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 16px',
      display: 'flex',
      justifyContent: isMobile ? 'flex-start' : 'space-between',
      alignItems: 'center',
      gap: isMobile ? '12px' : '0',
    },
    logo: {
      fontSize: isMobile ? '20px' : '24px',
      fontWeight: 700,
      color: '#14B8A6',
      textDecoration: 'none',
      letterSpacing: '-0.5px',
    },
    navLinks: {
      display: 'flex',
      gap: '32px',
      alignItems: 'center',
    },
    navLink: {
      color: '#5f6368',
      textDecoration: 'none',
      fontSize: '14px',
      fontWeight: 500,
      transition: 'color 0.2s',
    },
    mobileMenu: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      width: '80%',
      maxWidth: '400px',
      height: '100vh',
      background: '#ffffff',
      zIndex: 2000,
      padding: '80px 24px 24px',
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '24px',
    },
    mobileMenuOverlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      width: '100%',
      height: '100vh',
      background: 'rgba(0,0,0,0.5)',
      zIndex: 1999,
    },
    hamburger: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '8px',
    },
    closeButton: {
      position: 'absolute' as const,
      top: '16px',
      right: '16px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '8px',
    },
    primaryButton: {
      background: '#14B8A6',
      color: '#ffffff',
      padding: '12px 24px',
      borderRadius: '24px',
      textDecoration: 'none',
      fontSize: '14px',
      fontWeight: 600,
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)',
    },
    container: {
      minHeight: 'calc(100vh - 60px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #f0fdfa 0%, #dbeafe 50%, #fae8ff 100%)',
      padding: '24px',
      fontFamily: 'Google Sans, Roboto, Arial, sans-serif',
    },
    card: {
      background: '#ffffff',
      borderRadius: '16px',
      padding: isMobile ? '24px 16px' : '48px',
      maxWidth: '900px',
      width: '100%',
      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
    },
    title: {
      fontSize: isMobile ? '28px' : '48px',
      fontWeight: 400,
      color: '#202124',
      marginBottom: '12px',
      textAlign: 'center' as const,
      letterSpacing: '-0.5px',
    },
    subtitle: {
      fontSize: isMobile ? '15px' : '18px',
      color: '#5f6368',
      textAlign: 'center' as const,
      marginBottom: isMobile ? '28px' : '48px',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '24px',
    },
    optionCard: {
      background: '#ffffff',
      border: '2px solid #e8eaed',
      borderRadius: '12px',
      padding: '32px',
      cursor: 'pointer',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      textAlign: 'center' as const,
    },
    iconWrapper: {
      width: '64px',
      height: '64px',
      borderRadius: '16px',
      background: 'linear-gradient(135deg, #14B8A6 0%, #0891B2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 24px',
    },
    optionTitle: {
      fontSize: '24px',
      fontWeight: 500,
      color: '#202124',
      marginBottom: '12px',
    },
    optionDescription: {
      fontSize: '14px',
      color: '#5f6368',
      lineHeight: 1.6,
      marginBottom: '24px',
    },
    button: {
      background: '#14B8A6',
      color: '#ffffff',
      padding: '12px 24px',
      borderRadius: '24px',
      fontSize: '14px',
      fontWeight: 600,
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)',
    },
    backLink: {
      textAlign: 'center' as const,
      marginTop: '32px',
      fontSize: '14px',
      color: '#5f6368',
    },
    link: {
      color: '#14B8A6',
      textDecoration: 'none',
      fontWeight: 600,
    },
  };

  const options = [
    {
      icon: <Users size={32} color="#ffffff" />,
      title: 'HUSLRZ User',
      description: 'Join as a fitness enthusiast and access gyms nationwide',
      route: '/user-signup',
      color: '#14B8A6',
      disabled: false,
      isUser: true,
    },
    {
      icon: <Building2 size={32} color="#ffffff" />,
      title: 'HUSLRZ Partner',
      description: 'Register your gym and start growing your business',
      route: '/partner-signup',
      color: '#3B82F6',
      disabled: false,
      isUser: false,
    },
    {
      icon: <Briefcase size={32} color="#ffffff" />,
      title: 'HUSLRZ Corporate',
      description: 'Set up corporate wellness programs for your employees',
      route: '/corporate-login',
      color: '#8B5CF6',
      disabled: false,
      isUser: false,
    },
  ];

  return (
    <>
      <Helmet>
        <title>Sign Up — HUSLRZ</title>
      </Helmet>
      {/* Navigation */}
      <nav style={styles.nav}>
        <div style={styles.navContainer}>
          {isMobile && (
            <button style={styles.hamburger} onClick={() => setMobileMenuOpen(true)}>
              <Menu size={24} color="#202124" />
            </button>
          )}
          
          <Link to="/" style={styles.logo}>HUSLRZ</Link>
          
          {!isMobile && (
            <div style={styles.navLinks}>
              <Link to="/partners" style={styles.navLink}>For Partners</Link>
              <Link to="/corporate" style={styles.navLink}>For Corporates</Link>
              <Link to="/about" style={styles.navLink}>About</Link>
              <Link to="/contact" style={styles.navLink}>Contact</Link>
              <Link to="/login" style={styles.navLink}>Login</Link>
              <Link to="/signup" style={{ ...styles.navLink, color: '#14B8A6', fontWeight: 600 }}>
                Get Started
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobile && mobileMenuOpen && (
        <>
          <div style={styles.mobileMenuOverlay} onClick={() => setMobileMenuOpen(false)} />
          <div style={styles.mobileMenu}>
            <button style={styles.closeButton} onClick={() => setMobileMenuOpen(false)}>
              <X size={24} color="#202124" />
            </button>
            <Link to="/partners" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>For Partners</Link>
            <Link to="/corporate" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>For Corporates</Link>
            <Link to="/about" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>About</Link>
            <Link to="/contact" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>Contact</Link>
            <Link to="/login" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>Login</Link>
            <Link to="/signup" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0', color: '#14B8A6', fontWeight: 600 }} onClick={() => setMobileMenuOpen(false)}>Get Started</Link>
          </div>
        </>
      )}

      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.title}>
            Join <span style={{ color: '#14B8A6' }}>HUSLRZ</span>
          </h1>
          <p style={styles.subtitle}>Choose your signup type to get started</p>

          <div style={styles.grid}>
            {options.map((option, index) => (
              <div
                key={index}
                className="animate-fade-in-up"
                style={{
                  ...styles.optionCard,
                  animationDelay: `${index * 0.1}s`,
                  cursor: option.isUser ? 'default' : 'pointer',
                }}
                onClick={() => !option.isUser && navigate(option.route)}
                onMouseEnter={(e) => {
                  if (!option.isUser) {
                    e.currentTarget.style.borderColor = option.color;
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!option.isUser) {
                    e.currentTarget.style.borderColor = '#e8eaed';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                <div style={styles.iconWrapper}>{option.icon}</div>
                <h3 style={styles.optionTitle}>{option.title}</h3>
                <p style={styles.optionDescription}>{option.description}</p>
                <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '10px', width: '100%' }}>
                <button 
                  onClick={() => alert(`${option.title} signup coming soon! Take our survey to get early access.`)}
                  style={{ ...styles.button, width: '100%', justifyContent: 'center', background: '#e8eaed', color: '#5f6368', border: 'none', cursor: 'not-allowed' }}
                  disabled
                >
                  Coming Soon <ArrowRight size={16} />
                </button>
                <div style={{ fontSize: '12px', color: '#5f6368', textAlign: 'center', marginTop: '8px' }}>
                  {option.isUser ? (
                    <>Take our <a href="/user-survey" style={{ color: '#14B8A6', textDecoration: 'none', fontWeight: 600 }}>fitness survey</a> to get early access</>
                  ) : option.title === 'HUSLRZ Partner' ? (
                    <>Register your interest via <a href="/gym-survey" style={{ color: '#14B8A6', textDecoration: 'none', fontWeight: 600 }}>partner survey</a></>
                  ) : (
                    <>Register your interest via <a href="/corporate-survey" style={{ color: '#14B8A6', textDecoration: 'none', fontWeight: 600 }}>corporate survey</a></>
                  )}
                </div>
              </div>
              </div>
            ))}
          </div>

          <div style={styles.backLink}>
            Already have an account?{' '}
            <a href="/login" style={styles.link}>
              Login here
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignupSelection;
